import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'
import GettingStarted from '@segment/templates/GettingStarted'

import HowItWorks from 'views/Catalog/SinglePage/components/HowItWorks'
import SimilarIntegrations from 'views/Catalog/SinglePage/components/SimilarIntegrations'
import Hero from 'views/Catalog/SinglePage/components/Hero'

import { useSingleIntegrationQuery, useSimilarIntegrationsQuery } from '@segment/api/routes/catalog/queries'

import Layout from '@segment/next-shared/layout'

import LogoTitleBody from './components/LogoTitleBody'

import ogImageJpeg from './assets/OgCatalog.png'

type CatalogIntegrationViewProps = {
  slug: string
  category: string
  random: boolean
  amount: number
}

const CatalogIntegrationView: FC<PropsWithChildren<CatalogIntegrationViewProps>> = ({
  slug,
  category,
  random,
  amount,
}) => {
  const theme = useTheme()
  const { data } = useSingleIntegrationQuery({ args: [slug] })
  // If we can't find the integration we really can't render anything

  const { data: similarIntegrations } = useSimilarIntegrationsQuery({ args: [{ slug, category, random, amount }] })

  if (!data || !similarIntegrations) return null

  const name = data?.name
  const introduction = data?.introduction

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={`${name} Integration`}
        pageDescription={`Learn more about ${name} on Segment. ${name}: ${introduction}`}
        pageOG={ogImageJpeg}
        bodyBackground={theme.palette.neutrals.white[100]}
      >
        <Navigation />
        <Main>
          <Hero integration={data} />
          {data?.withoutSegment && <HowItWorks integration={data} />}

          {data?.withSegment && <LogoTitleBody integration={data} />}
          {Boolean(similarIntegrations?.length) && <SimilarIntegrations entries={similarIntegrations} />}
          <GettingStarted
            heading={`Integrate ${name} with Segment`}
            description={`Segment makes it easy to set up ${name}.`}
          />
        </Main>
      </Layout>
      <Footer />
    </AppearanceProvider>
  )
}

const Main = styled.main``

export default CatalogIntegrationView
